import { memo, useState } from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import _toLower from 'lodash/toLower';
import _trim from 'lodash/trim';
import { useNavigate, useLocation } from 'react-router-dom';
// Local files
import ErrorWrapper from 'components/Common/ErrorWrapper/ErrorWrapper';
import { checkValidEmail } from 'helpers';
import useSuccess from 'hooks/useSuccess';
import useSessions from 'hooks/useSessions';
import useManagers from 'hooks/useManagers';
import useConfirmations from 'hooks/useConfirmations';

const Form = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { createSession } = useSessions();
  const { sendConfirmationCode } = useConfirmations();
  const { createManager } = useManagers();
  const { setSuccess } = useSuccess();
  const [email, setEmail] = useState({ value: '', error: '' });
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState({ value: '', error: '' });
  const [showPasswordSymbols, setShowPasswordSymbols] = useState(false);
  const [confirmationCode, setConfirmationCode] = useState('');
  const [step, setStep] = useState('send');
  const [processing, setProcessing] = useState(false);
  const isCreateManager = !!state?.invitation_token;

  const handleSubmitClick = () => {
    let hasError = false;

    if (!checkValidEmail(email.value)) {
      setEmail(prev => ({ ...prev, error: 'Email is invalid' }) );
      hasError = true;
    }
    if (!password.value) {
      setPassword(prev => ({ ...prev, error: 'Password can\'t be empty' }));
      hasError = true;
    }
    if (hasError) return;

    const session = { email: _trim(_toLower(email.value)), password: password.value, confirmation_code: confirmationCode };
 
    setProcessing(true);
    if (step === 'code'){
      sendConfirmationCode(session)
      .then(({ payload, error }) => {
        if (!error) {
          if (payload?.status === 204) setStep('code');
        }
      })
      .finally(() => setProcessing(false))
    } else {
      createSession(session)
      .then(({ error }) => {
        if (!error) {
          navigate('/');
        }
      })
      .finally(() => setProcessing(false))
    }
  };
  const handleCreate = () => {
    let hasError = false;

    if (!checkValidEmail(email.value)) {
      setEmail(prev => ({ ...prev, error: 'Email is invalid' }) );
      hasError = true;
    }
    if (!password.value) {
      setPassword(prev => ({ ...prev, error: `Password can't be empty` }));
      hasError = true;
    }
    if (hasError) return;

    const manager = { email: _trim(_toLower(email.value)), first_name: firstName || null, last_name: lastName || null, password: password.value };

    setProcessing(true);
    createManager({ manager, invitation_token: state?.invitation_token })
    .then(({ error }) => {
      if (!error) {
        setSuccess('Successfully created');
        navigate('/sign_in');
      }
    })
    .finally(() => setProcessing(false))
  };
  const handleKeyDown = ({ key }) => !processing && key === 'Enter' && (isCreateManager ? handleCreate() : handleSubmitClick());

  return (
    <Box sx={{ width: '100%' }}>
      {step === 'code' && <>
        <Typography align='center' py={3}>ENTER YOUR 5 DIGIT CODE</Typography>
        <TextField
          fullWidth
          margin='normal'
          value={confirmationCode}
          onChange={({ target: { value } }) => setConfirmationCode(value)}
          onKeyDown={handleKeyDown}
          inputProps={{ maxLength: 5 }}
        />
        <Typography align='center' color='textSecondary' py={2}>Your code was sent to {email.value}</Typography>
      </>}
      {step === 'send' && <>
        {isCreateManager && <><TextField
          fullWidth
          margin='normal'
          placeholder='First Name'
          value={firstName}
          onChange={({ target: { value } }) => setFirstName(value)}
          onKeyDown={handleKeyDown}
        />
        <TextField
          fullWidth
          margin='normal'
          placeholder='Last Name'
          value={lastName}
          onChange={({ target: { value } }) => setLastName(value)}
          onKeyDown={handleKeyDown}
        /></>}
        <TextField
          fullWidth
          margin='normal'
          placeholder='Enter email'
          type='email'
          value={email.value}
          onChange={({ target: { value } }) => setEmail({ value, error: '' })}
          error={!!email.error}
          helperText={email.error}
          onKeyDown={handleKeyDown}
        />
        <TextField
          fullWidth
          margin='dense'
          placeholder='Enter password'
          type={showPasswordSymbols ? 'text' : 'password'}
          value={password.value}
          onChange={({ target: { value } }) => setPassword({ value, error: '' })}
          error={!!password.error}
          helperText={password.error}
          onKeyDown={handleKeyDown}
          InputProps={{
            endAdornment: (
              <IconButton
                aria-label="show password"
                color='primary'
                disableRipple
                onClick={() => setShowPasswordSymbols(!showPasswordSymbols)}
                sx={{
                    position: 'absolute', right: '8px'
                }}
              >
                {showPasswordSymbols ? <VisibilityIcon /> : <VisibilityOffIcon />}
              </IconButton>
            ),
            sx: {
              pr: 0,
              '& input': { pr: '48px' }
            }
          }}
        />
      </>}
      <Button
        sx={{ mt: 2, mb: 2 }}
        fullWidth
        size='large'
        variant='contained'
        disabled={(step === 'code' && confirmationCode.length !== 5) || processing}
        onClick={isCreateManager ? handleCreate : handleSubmitClick}
      >
        {isCreateManager ? 'Sign Up' : 'Sign In'}
      </Button>
    </Box>
  );
};

const WrappedComponent = props => {
  return (
    <ErrorWrapper>
      <Form {...props} />
    </ErrorWrapper>
  );
};

export default memo(WrappedComponent);